import { apiFetch, staticFetch, TEMPLATES_BASE_URL } from "../api.js";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const joinBaseUrl = (baseUrl, assetPath) => {
    const rawPath = String(assetPath || "").trim();
    if (!rawPath) return "";
    if (isAbsoluteUrl(rawPath)) return rawPath;

    const cleanBase = String(baseUrl || "").replace(/\/+$/, "");
    const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `${cleanBase}${cleanPath}`;
};

const withQueryFlag = (url, key, value) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const cloneValue = (value) => JSON.parse(JSON.stringify(value || {}));
const OPTIONAL_SECTION_KEY_PREFIXES = ["sectionVisibility:", "sectionOrder:"];

const extractOptionalSectionState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        const key = String(field || "").trim();
        if (!OPTIONAL_SECTION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
        next[key] = cloneValue(entry);
    }

    return next;
};

const withOptionalSectionState = (url, value) => {
    const rawUrl = String(url || "").trim();
    if (!rawUrl) return rawUrl;

    const optionalSections = extractOptionalSectionState(value);
    if (!Object.keys(optionalSections).length) return rawUrl;

    try {
        const nextUrl = new URL(rawUrl, window.location.origin);
        nextUrl.searchParams.set("heyweb_sections", JSON.stringify(optionalSections));
        return nextUrl.toString();
    } catch {
        return rawUrl;
    }
};

const isSharedStateField = (field) => {
    const key = String(field || "").trim().toLowerCase();
    if (!key) return false;
    if (key.startsWith("sectionvisibility:")) return true;
    if (key.startsWith("sectionorder:")) return true;
    if (key.startsWith("header:global:")) return true;
    if (key.startsWith("banner:")) return true;
    if (key.startsWith("logo:")) return true;
    if (key.startsWith("i18n:header.")) return true;
    if (key.startsWith("i18n:search.")) return true;
    if (key.startsWith("i18n:locale.")) return true;
    if (key.includes(":headerglobal:")) return true;
    return false;
};

const extractSharedState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        if (!isSharedStateField(field)) continue;
        next[field] = cloneValue(entry);
    }

    return next;
};

const mergeEditorState = (pageContent, editorBackup) => {
    const safePageContent = pageContent && typeof pageContent === "object" ? cloneValue(pageContent) : {};
    const safeBackup = extractSharedState(editorBackup);
    const next = { ...safePageContent };

    Object.entries(safeBackup).forEach(([field, entry]) => {
        if (
            entry && typeof entry === "object" && !Array.isArray(entry) &&
            next[field] && typeof next[field] === "object" && !Array.isArray(next[field])
        ) {
            next[field] = {
                ...next[field],
                ...entry,
            };
            return;
        }

        next[field] = entry;
    });

    return next;
};

const normalizeTemplateCollection = (catalog) => (Array.isArray(catalog) ? catalog : []);

const getTemplateIdentity = (template) =>
    template?.manifest?.id ||
    template?.id ||
    template?.template_id ||
    template?.templateId ||
    template?.slug ||
    template?.vertical ||
    template?.name ||
    template?.title ||
    "";

const parseResponseBody = async (response) => {
    const text = await response.text();
    if (!text) return null;

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

const ensureOk = async (response, fallbackMessage) => {
    if (response.ok) {
        return parseResponseBody(response);
    }

    const body = await parseResponseBody(response);
    const message =
        body?.message ||
        body?.error ||
        (typeof body === "string" ? body : "") ||
        fallbackMessage ||
        `HTTP ${response.status}`;

    throw new Error(message);
};

export const SitePreviewView = {
    state: {
        previewReady: false,
        previewWin: null,
        currentData: {},
    },

    render: async () => `
        <section style="max-width:1440px; margin:0 auto; padding:1.25rem 1.25rem 2rem;">
            <div style="display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:1rem; flex-wrap:wrap;">
                <div>
                    <div style="font-size:0.78rem; font-weight:800; letter-spacing:0.08em; text-transform:uppercase; color:#64748b;">Visualizacao do site</div>
                    <h1 id="site-preview-title" style="margin:0.25rem 0 0; font-size:1.9rem; line-height:1.1; color:#0f172a;">A carregar...</h1>
                </div>
                <div style="display:flex; align-items:center; gap:0.75rem; flex-wrap:wrap;">
                    <span id="site-preview-status" style="display:inline-flex; align-items:center; gap:0.55rem; min-height:2.75rem; padding:0 0.95rem; border-radius:999px; border:1px solid #dbe4f0; background:white; color:#475569; font-size:0.92rem; font-weight:700;">
                        <span id="site-preview-status-dot" style="width:0.62rem; height:0.62rem; border-radius:50%; background:#94a3b8;"></span>
                        <span id="site-preview-status-text">A preparar site</span>
                    </span>
                    <a id="site-preview-edit-link" href="#dashboard" style="display:inline-flex; align-items:center; justify-content:center; min-height:2.75rem; padding:0 1.05rem; border-radius:0.95rem; border:1px solid #dbe4f0; background:white; color:#0f172a; text-decoration:none; font-weight:800;">
                        Editar Site
                    </a>
                </div>
            </div>

            <div style="width:100%; min-height:calc(100vh - 12rem); border:1px solid #dbe4f0; background:white; overflow:hidden;">
                <iframe id="site-view-frame" title="Visualizacao do site" style="display:block; width:100%; min-height:calc(100vh - 12rem); border:0; background:white;"></iframe>
            </div>
        </section>
    `,

    afterRender: async (params) => {
        const sitePath = decodeURIComponent(params[0] || "").trim();
        if (!sitePath) {
            window.location.hash = "#dashboard";
            return;
        }

        const titleEl = document.getElementById("site-preview-title");
        const statusDot = document.getElementById("site-preview-status-dot");
        const statusText = document.getElementById("site-preview-status-text");
        const editLink = document.getElementById("site-preview-edit-link");
        const iframe = document.getElementById("site-view-frame");

        if (editLink) {
            editLink.href = `#editor/site/${encodeURIComponent(sitePath)}`;
        }

        try {
            const [siteResponse, catalogResponse] = await Promise.all([
                apiFetch(`/my-sites/${encodeURIComponent(sitePath)}`, { method: "GET" }),
                staticFetch(`/templates.json?v=${Date.now()}`),
            ]);

            const site = await ensureOk(siteResponse, "Nao foi possivel carregar o site.");
            const catalog = catalogResponse.ok ? normalizeTemplateCollection(await catalogResponse.json()) : [];

            let currentData = cloneValue(site?.extra?.editor_content || {});
            try {
                const homePageResponse = await apiFetch(`/my-sites/${encodeURIComponent(sitePath)}/pages/home`, { method: "GET" });
                const homePage = await ensureOk(homePageResponse, "Nao foi possivel carregar a pagina principal.");
                currentData = mergeEditorState(homePage?.content, site?.extra?.editor_content);
            } catch (error) {
                currentData = cloneValue(site?.extra?.editor_content || {});
            }

            const selectedTemplate = catalog.find((template) => {
                const templateId = getTemplateIdentity(template);
                return (
                    templateId === site?.template_id ||
                    template?.vertical === site?.template_id ||
                    template?.slug === site?.template_id
                );
            });

            if (!selectedTemplate) {
                throw new Error("Template associado ao site nao foi encontrado no catalogo.");
            }

            SitePreviewView.state.currentData = currentData;

            if (titleEl) {
                titleEl.innerText = site?.name || selectedTemplate?.name || sitePath;
            }

            const previewPath =
                selectedTemplate?.preview_url ||
                selectedTemplate?.url ||
                site?.url ||
                `/${sitePath}/`;
            const previewUrl = withOptionalSectionState(
                withQueryFlag(joinBaseUrl(TEMPLATES_BASE_URL, previewPath), "siteeiro_preview", "1"),
                SitePreviewView.state.currentData
            );

            iframe.src = previewUrl;

            const handleMessage = (event) => {
                if (event.origin !== new URL(TEMPLATES_BASE_URL).origin) return;
                if (!event.data || event.data.type !== "HEYWEB_READY") return;

                SitePreviewView.state.previewReady = true;
                SitePreviewView.state.previewWin = iframe.contentWindow;
                statusDot.style.background = "#10b981";
                if (statusText) statusText.textContent = "Site pronto para visualizacao";

                SitePreviewView.state.previewWin?.postMessage(
                    {
                        type: "HEYWEB_INIT",
                        data: SitePreviewView.state.currentData,
                    },
                    "*"
                );
            };

            iframe.onload = () => {
                if (!SitePreviewView.state.previewReady) {
                    statusDot.style.background = "#3b82f6";
                    if (statusText) statusText.textContent = "A aplicar conteudo guardado";
                }
            };

            window.addEventListener("message", handleMessage);
            SitePreviewView._cleanup = () => {
                window.removeEventListener("message", handleMessage);
            };
        } catch (error) {
            console.error("Erro ao abrir visualizacao do site:", error);

            const app = document.getElementById("app");
            if (!app) return;

            app.innerHTML = `
                <section style="max-width:960px; margin:0 auto; padding:3rem 1.25rem;">
                    <div style="padding:2rem; background:#fff1f2; border:1px solid #fecdd3; color:#9f1239;">
                        <h1 style="margin:0 0 0.75rem; font-size:1.5rem;">Nao foi possivel abrir este site</h1>
                        <p style="margin:0 0 1rem; color:#9f1239;">${error.message || "Erro inesperado."}</p>
                        <a href="#dashboard" style="display:inline-flex; align-items:center; justify-content:center; min-height:2.75rem; padding:0 1rem; border-radius:0.9rem; background:#0f172a; color:white; text-decoration:none; font-weight:800;">
                            Voltar ao dashboard
                        </a>
                    </div>
                </section>
            `;
        }
    },
};

